import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Car, CheckCircle, FileText, Clock, Star, MapPin, Facebook, ShieldCheck, DollarSign, Wrench, Gauge, ThumbsUp, Activity, BadgeCheck, CalendarClock, ScanLine, ArrowRight, Image as ImageIcon, AlertTriangle, HelpCircle, Camera } from 'lucide-react';
import { MapContainer, TileLayer, Circle, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import BookingNotification from '../components/BookingNotification';
import BrandHero from '../components/BrandHero';
import BuyerJourney from '../components/BuyerJourney';
import { Seo } from '../components/academy/Seo';
import { SITEWIDE_SCHEMAS } from '../data/seoSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const homeScoreColor = (s) => (s >= 85 ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : s >= 70 ? 'text-amber-400 border-amber-500/40 bg-amber-500/10' : 'text-orange-400 border-orange-500/40 bg-orange-500/10');

const GOOGLE_REVIEWS_URL = 'https://share.google/6G8NttFgPUaX5zKRF';
const HOME_REVIEWS = [
  { name: 'dexter04', initial: 'D', rating: 5, body: '10 out of 10, will recommend. I was looking to purchase a vehicle and CarLens Auto Inspections took the fear out of buying a used vehicle — very professional, very thorough, and made the whole process very easy. Put me at ease with all the horror stories you hear. From now on, anytime I go to buy a car I\u2019ll definitely be using them.' },
  { name: 'Mehdi B.', initial: 'M', rating: 5, body: 'Amazing service that I cannot ever imagine buying another used car without. Saved me thousands of dollars and a potential headache.' },
  { name: 'Evelina G.', initial: 'E', rating: 5, body: 'He did an inspection on a used car I was interested in buying. He was very helpful and explained everything in detail, and was honest too! Would totally recommend him! Great customer service!' },
  { name: 'Steve R.', initial: 'S', rating: 5, body: 'My technician was very professional and provided the most detailed report I\u2019ve ever seen on the condition of the truck I was interested in buying. Answered all of my questions.' },
  { name: 'JT D.', initial: 'J', rating: 5, body: 'One of my players was looking to purchase a car and Daniel was amazing in all of his help. I would absolutely recommend them!' },
  { name: 'Trace D.', initial: 'T', rating: 5, body: 'Had them do an inspection on a vehicle I already own and they were very insightful and helpful — pointed out things I wouldn\u2019t have caught on my own.' },
];

const HOME_FAQS = [
  {
    q: 'How much does a pre-purchase car inspection cost in Jacksonville, Florida?',
    a: 'A CarLens Certified Pre-Purchase Inspection is $199, and our Exotic Inspection for luxury and high-performance vehicles is $299. Both include a full 200-point inspection, 30–50 photos, an OBD-II diagnostic scan, and a detailed written Vehicle Decision Report™ — with no dealership markup or hidden fees. A live report review and consultation with your inspector is an optional $29 add-on.',
  },
  {
    q: "What is included in a CarLens pre-purchase vehicle inspection?",
    a: 'Every inspection covers a 200-point evaluation of the engine, transmission, brakes, suspension, tires (with measured tread depth), exterior and interior condition, and a live OBD-II diagnostic scan for stored and pending fault codes. You receive a plain-English executive summary, a 24-month ownership cost forecast, 30–50 high-resolution photos, and a final CarLens Confidence Score™ with a clear buy / negotiate / walk-away recommendation.',
  },
  {
    q: 'Do you come to the seller or the dealership to inspect the car?',
    a: 'Yes. CarLens is a fully mobile inspection service. Our certified inspectors travel to the seller\u2019s home, the dealership lot, or any agreed meeting point across Northeast Florida so you never have to move the vehicle or take time off work. We inspect cars at private-seller homes, franchise and independent dealerships, and Facebook Marketplace meetups.',
  },
  {
    q: 'How long does a used car inspection take?',
    a: 'A standard pre-purchase inspection takes roughly 60–90 minutes on-site depending on the vehicle. Your written Vehicle Decision Report™ with photos is typically delivered the same day, and same-day priority service is available in select areas.',
  },
  {
    q: 'Can you inspect a car I found on Facebook Marketplace or from a private seller?',
    a: 'Absolutely — private-seller and Facebook Marketplace purchases are exactly where an independent inspection pays for itself. Because there is no dealer warranty or return window, CarLens gives you an unbiased, third-party assessment before you hand over any money, plus a negotiation worksheet showing where the price should land.',
  },
  {
    q: 'How soon can I get my inspection report?',
    a: 'Most CarLens reports are delivered the same day as the inspection. If you are on a tight timeline with a seller, our Priority Same-Day option (where eligible) gets an inspector out and a report back to you within hours so you can make a confident decision fast.',
  },
  {
    q: 'What areas in Northeast Florida does CarLens serve?',
    a: 'CarLens serves Jacksonville, St. Augustine, Orange Park, Fleming Island, Middleburg, Fernandina Beach, Ponte Vedra Beach, Green Cove Springs, and the surrounding Northeast Florida and South Georgia communities. If you are unsure whether we cover your area, just ask when you book.',
  },
  {
    q: 'Is a pre-purchase inspection really worth it?',
    a: 'For a used vehicle, yes. A single overlooked issue — worn brakes, a failing transmission, hidden accident damage, or an active check-engine code — can cost thousands after purchase. For a small fraction of the vehicle\u2019s price, a CarLens inspection either confirms the car is a smart buy or hands you documented leverage to negotiate a lower price or walk away entirely.',
  },
];

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [recentInspections, setRecentInspections] = useState([]);

  useEffect(() => {
    axios.get(`${API}/recent-inspections`)
      .then((res) => setRecentInspections(res.data || []))
      .catch(() => setRecentInspections([]));
  }, []);

  const recentPhotos = recentInspections
    .flatMap((it) => (it.photos || []).map((url) => ({ url, vehicle: it.vehicle, city: it.city })))
    .slice(0, 8);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <Seo
        siteName="CarLens"
        title="Pre-Purchase Car Inspections in Jacksonville, FL | CarLens"
        description="Independent mobile pre-purchase vehicle inspections in Jacksonville & Northeast Florida. 200-point inspection, same-day report, from $199. Know the truth before you buy."
        path="/"
        faq={HOME_FAQS}
        extraSchemas={SITEWIDE_SCHEMAS}
      />
      {/* Booking Notification Popup */}
      <BookingNotification />

      {/* Hero Banner — CARLENS brand hero */}
      <BrandHero />

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-8 pb-12 md:pt-12 md:pb-16">
        {/* Full-width heading */}
        <div className="text-center max-w-5xl mx-auto mb-10 md:mb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08]" style={{ fontFamily: 'Space Grotesk' }}>
            Independent Pre-Purchase Vehicle Inspections in Jacksonville, Florida
          </h1>
          <p className="mt-5 text-xl md:text-2xl text-orange-400 font-semibold tracking-tight">
            Know the Truth Before You Buy.
          </p>
          <p className="text-base md:text-xl text-zinc-300 leading-relaxed mt-5 max-w-3xl mx-auto">
            CarLens is Northeast Florida&rsquo;s independent, mobile <strong className="text-white">pre-purchase car inspection</strong> service. Our certified inspectors come to the seller, dealership, or private-party location across Jacksonville, St. Augustine, Orange Park, and the surrounding areas &mdash; delivering a detailed 200-point Vehicle Decision Report&trade; so you buy with confidence, not regret.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/20"
              onClick={() => navigate('/book')}
              data-testid="book-inspection-hero-btn"
            >
              Book Inspection Now
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-white/5 px-8 py-6 text-lg rounded-lg"
              onClick={() => navigate('/what-you-receive')}
              data-testid="view-sample-report-btn"
            >
              View Sample Report
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10 px-8 py-6 text-lg rounded-lg"
              onClick={() => navigate('/recent-inspections')}
              data-testid="see-recent-inspections-btn"
            >
              See Recent Inspections
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Stage Buyer Support Funnel — directly under the hero CTA */}
      <BuyerJourney />

      {/* Sample report + By The Numbers */}
      <section className="max-w-7xl mx-auto px-6 pb-16 md:pb-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto">
          {/* Sample report preview */}
          <div className="relative mx-auto w-full max-w-sm lg:max-w-md" data-testid="hero-exec-summary">
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/30 to-zinc-600/20 rounded-3xl blur-3xl"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/sdaxej2l_47BB2B63-4434-420A-B52E-947A20C95406.png" 
              alt="CarLens Vehicle Executive Summary sample"
              className="relative rounded-2xl shadow-2xl border-2 border-zinc-700/60 w-full h-auto cursor-pointer hover:scale-[1.03] transition-transform duration-300"
              onClick={() => navigate('/what-you-receive')}
            />
          </div>

          {/* CarLens By The Numbers */}
          <div className="rounded-3xl border border-orange-500/20 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 md:p-10 text-left shadow-2xl shadow-black/40" data-testid="by-the-numbers">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3" style={{ fontFamily: 'Space Grotesk' }}>
              <ShieldCheck className="w-7 h-7 text-orange-400" /> CarLens By The Numbers
            </h3>
            <ul className="space-y-4">
              {[
                '10+ Years Inspection Experience',
                'Thousands of Vehicles Inspected',
                '200+ Point Inspection',
                'Independent & Unbiased',
                'Same-Day Reports Available',
                '10+ Page Reports · 30–50 Photos',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-100" data-testid={`by-the-numbers-item-${i}`}>
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-lg md:text-xl font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CarLens FirstLook — lead-gen section */}
      <section className="relative overflow-hidden" data-testid="firstlook-home-section" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #1c1c1f 50%, #0e0e10 100%)' }}>
        <div className="absolute -top-24 right-1/4 w-[500px] h-[280px] bg-orange-500/15 blur-[130px] rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-orange-300 mb-5">
                <ScanLine className="w-4 h-4" /> CarLens FirstLook™
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.08] tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
                Found a Used Car Online?<br />
                <span className="text-orange-500">Let CarLens take the first look.</span>
              </h2>
              <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-xl">
                Send us the listing, VIN, price, and photos and book a private 30-minute video consultation. A CarLens expert reviews the vehicle with you live — history, price, red flags, questions to ask, and next steps — before you buy. Virtual, nationwide, $49.
              </p>
              <button
                onClick={() => navigate('/firstlook')}
                className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-8 py-4 rounded-full shadow-lg shadow-orange-500/25 transition-all hover:scale-[1.02]"
                data-testid="firstlook-cta"
              >
                Book Your FirstLook — $49
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { Icon: Gauge, t: 'Pricing & Mileage', d: 'Is the asking price and mileage reasonable?' },
                { Icon: ImageIcon, t: 'Photo Review', d: 'What the listing photos reveal — and hide.' },
                { Icon: AlertTriangle, t: 'Warning Signs', d: 'Red flags worth a closer look.' },
                { Icon: HelpCircle, t: 'Seller Questions', d: 'Exactly what to ask before you go.' },
              ].map((c) => (
                <div key={c.t} className="rounded-2xl border border-zinc-800 bg-[#161618] p-5">
                  <div className="w-10 h-10 grid place-items-center rounded-xl bg-orange-500/15 text-orange-400 mb-3"><c.Icon className="w-5 h-5" /></div>
                  <p className="font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{c.t}</p>
                  <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Why It Matters Section */}
      <section className="max-w-7xl mx-auto px-6 pt-4 pb-16" data-testid="why-it-matters-section">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12" style={{ fontFamily: 'Space Grotesk' }}>
          Buying a used vehicle shouldn't feel like gambling.
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 hover:border-orange-500/50 transition-all" data-testid="value-independent">
            <div className="w-14 h-14 rounded-xl bg-zinc-600/10 flex items-center justify-center mb-5">
              <ShieldCheck className="w-7 h-7 text-zinc-300" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Independent Inspection</h3>
            <p className="text-zinc-400 leading-relaxed">No dealerships. No sales commission. Just the truth.</p>
          </div>
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 hover:border-orange-500/50 transition-all" data-testid="value-ownership-guidance">
            <div className="w-14 h-14 rounded-xl bg-orange-500/10 flex items-center justify-center mb-5">
              <DollarSign className="w-7 h-7 text-orange-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Ownership Guidance</h3>
            <p className="text-zinc-400 leading-relaxed">Know what it'll really cost to own before you commit.</p>
          </div>
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 hover:border-orange-500/50 transition-all" data-testid="value-ownership-costs">
            <div className="w-14 h-14 rounded-xl bg-green-500/10 flex items-center justify-center mb-5">
              <Wrench className="w-7 h-7 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Future Ownership Costs</h3>
            <p className="text-zinc-400 leading-relaxed">Understand what you'll likely spend over the next two years.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gradient-to-b from-zinc-50 to-white py-24" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              How It Works
            </h2>
            <p className="text-zinc-500 text-lg">Simple, fast, and transparent process</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-zinc-600 to-zinc-700 flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold shadow-lg shadow-zinc-600/20">
                1
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 mb-4">Book a CarLens Inspection</h3>
              <p className="text-zinc-600">Schedule your inspection online in minutes. Choose your tier, pick a date and time, and we'll come to you or the seller's location.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold shadow-lg shadow-orange-500/20">
                2
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 mb-4">Receive Your Vehicle Decision Report™</h3>
              <p className="text-zinc-600">Our certified technician performs a comprehensive 200+ point inspection and delivers a detailed report with photos, findings, and clear recommendations.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold shadow-lg shadow-green-500/20">
                3
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 mb-4">Buy With Confidence</h3>
              <p className="text-zinc-600">Know exactly what you're buying—and what it'll cost to own. Drive away with peace of mind, not regret.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Your Report Includes - 8 Categories */}
      <section className="pt-4 pb-20" data-testid="report-includes-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              What's Included In Your Report
            </h2>
            <p className="text-zinc-400 text-lg">A comprehensive 200-point inspection, distilled into 8 clear sections</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FileText, label: 'Executive Summary', desc: 'A plain-English verdict on the vehicle.', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'hover:border-sky-500/50' },
              { icon: Car, label: 'Vehicle Snapshot', desc: 'Year, make, model, mileage, VIN & color.', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'hover:border-orange-500/50' },
              { icon: CalendarClock, label: 'Upcoming Ownership', desc: "What you'll likely spend over 24 months.", color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'hover:border-emerald-500/50' },
              { icon: ScanLine, label: 'Exterior / Interior Conditions', desc: 'Body, paint, cabin & feature condition.', color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'hover:border-violet-500/50' },
              { icon: Wrench, label: 'Mechanical Health', desc: 'Engine, transmission, brakes & suspension.', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'hover:border-amber-500/50' },
              { icon: Gauge, label: 'Tread Depth', desc: 'Measured tire wear on all four corners.', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'hover:border-cyan-500/50' },
              { icon: Activity, label: 'OBD Diagnostics', desc: 'Live scan for stored & pending fault codes.', color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'hover:border-rose-500/50' },
              { icon: ThumbsUp, label: 'Final Recommendation', desc: 'Your overall CarLens Confidence Score™.', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'hover:border-teal-500/50' },
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-6 transition-all flex flex-col items-start ${item.border}`}
                data-testid={`report-item-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className={`font-bold text-lg tabular-nums ${item.color}`} style={{ fontFamily: 'Space Grotesk' }}>{String(i + 1).padStart(2, '0')}</span>
                  <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center`}>
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-white leading-snug mb-1">{item.label}</h3>
                <p className="text-sm text-zinc-400 leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-b from-white to-zinc-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              What Our Customers Say
            </h2>
            <div className="inline-flex items-center gap-3 rounded-full border border-zinc-200 bg-white px-5 py-2.5 shadow-sm" data-testid="google-rating-summary">
              <span className="text-2xl font-extrabold text-zinc-900" style={{ fontFamily: 'Space Grotesk' }}>5.0</span>
              <span className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />)}
              </span>
              <span className="text-zinc-500 text-sm"><span className="font-semibold text-zinc-700">5-star reviews</span></span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {HOME_REVIEWS.map((r) => (
              <div key={r.name} className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-shadow flex flex-col" data-testid={`review-card-${r.initial}`}>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <p className="text-zinc-700 mb-6 leading-relaxed flex-1">"{r.body}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shrink-0" style={{ fontFamily: 'Space Grotesk' }}>
                    {r.initial}
                  </div>
                  <div>
                    <div className="font-semibold text-zinc-900">{r.name}</div>
                    <div className="flex items-center gap-1.5 text-sm text-zinc-500">
                      <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_18dp.png" alt="Google" className="w-4 h-4" loading="lazy" />
                      Google review
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-zinc-900 text-white px-7 py-3.5 font-semibold hover:bg-zinc-800 transition-colors"
              data-testid="read-google-reviews-btn"
            >
              <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_18dp.png" alt="" className="w-5 h-5 bg-white rounded-full p-0.5" />
              Read all reviews on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Service Area Map Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              Our Service Area
            </h2>
            <p className="text-zinc-600 text-lg mb-2">
              <MapPin className="w-5 h-5 inline mr-2 text-orange-500" />
              Currently serving North Florida and South Georgia
            </p>
            <p className="text-zinc-500">
              Including Jacksonville, Gainesville, St. Augustine, Kingsland, Brunswick, and surrounding areas
            </p>
          </div>

          <div className="bg-gradient-to-br from-zinc-50 to-white rounded-2xl border-2 border-zinc-200 p-6 shadow-xl">
            <div className="h-96 rounded-xl overflow-hidden shadow-lg">
              <MapContainer 
                center={[30.7, -81.8]} 
                zoom={8} 
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {/* Jacksonville Circle */}
                <Circle 
                  center={[30.3322, -81.6557]} 
                  radius={80000}
                  pathOptions={{ 
                    color: '#f97316', 
                    fillColor: '#fb923c',
                    fillOpacity: 0.3 
                  }}
                >
                  <Popup>
                    <div className="text-center">
                      <strong>Jacksonville Area</strong>
                      <p className="text-sm">St. Augustine, Fernandina Beach</p>
                    </div>
                  </Popup>
                </Circle>
                
                {/* Kingsland/Brunswick Circle */}
                <Circle 
                  center={[30.8016, -81.6896]} 
                  radius={50000}
                  pathOptions={{ 
                    color: '#f97316', 
                    fillColor: '#fb923c',
                    fillOpacity: 0.3 
                  }}
                >
                  <Popup>
                    <div className="text-center">
                      <strong>South Georgia</strong>
                      <p className="text-sm">Kingsland, Brunswick area</p>
                    </div>
                  </Popup>
                </Circle>
              </MapContainer>
            </div>
            <div className="mt-6 grid md:grid-cols-2 gap-4 text-center">
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="font-semibold text-zinc-900 mb-1">North Florida</div>
                <div className="text-sm text-zinc-600">Jacksonville, St. Augustine, Gainesville, Fernandina Beach</div>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="font-semibold text-zinc-900 mb-1">South Georgia</div>
                <div className="text-sm text-zinc-600">Kingsland, Brunswick & nearby areas</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose CarLens */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Why Choose CarLens?
          </h2>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-6">
            CarLens helps drivers make smarter vehicle decisions through AI-powered recommendations and unbiased guidance from automotive professionals. From purchase decisions to long-term ownership questions, CarLens simplifies the vehicle journey and gives members confidence at every step.
          </p>
          <p className="text-zinc-400 text-lg max-w-3xl mx-auto leading-relaxed mb-3">
            What makes CarLens truly cutting-edge is our combination of top-notch certified inspectors and advanced AI technology. Our findings aren't based purely on human observation alone — every photo and data point is analyzed through artificial intelligence to detect issues that even the most experienced eye might miss.
          </p>
          <p className="text-zinc-400 text-lg max-w-3xl mx-auto leading-relaxed">
            This powerful fusion of expert human inspection and AI-powered analysis delivers the most thorough and comprehensive vehicle inspection reports available in the United States, giving you unparalleled confidence in your purchase decision.
          </p>
          <div className="mt-8 max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 border border-orange-500/30 rounded-xl p-6">
              <p className="text-zinc-300 text-base leading-relaxed text-center">
                Whether you're visiting a dealership or purchasing from a private seller, our certified inspectors come directly to your location. We provide convenient, on-site inspections at the seller's location, dealership lot, or any agreed-upon meeting point — making the inspection process seamless and hassle-free for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Long-form SEO content: Why an inspection matters in NE Florida */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="seo-longform-section">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
          Why a Pre-Purchase Inspection Matters in Northeast Florida
        </h2>
        <div className="space-y-5 text-zinc-300 text-base md:text-lg leading-relaxed">
          <p>
            Buying a used car in Jacksonville and the surrounding First Coast is often the second-largest
            purchase people make after a home &mdash; yet most buyers commit thousands of dollars based on a
            short test drive and a seller&rsquo;s word. Florida&rsquo;s heat, humidity, and coastal salt air are
            especially hard on vehicles: they accelerate rust on undercarriages, degrade rubber and cooling
            components, and stress air-conditioning systems. A car that looks flawless in the driveway can hide
            problems that only a trained inspector with the right tools will find.
          </p>
          <p>
            A CarLens <Link to="/services/pre-purchase-inspection" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">pre-purchase inspection</Link> removes
            the guesswork. Our ASE-minded, independent inspectors perform a full 200-point evaluation of the
            engine, transmission, brakes, suspension, steering, tires, and electrical systems, then run a live
            OBD-II diagnostic scan to surface stored and pending trouble codes the dash light may not yet be
            showing. Because we work only for you &mdash; never the dealership or the seller &mdash; there is no
            incentive to gloss over problems or push a sale.
          </p>
          <p>
            You receive a detailed Vehicle Decision Report&trade; the same day: a plain-English executive summary,
            30&ndash;50 high-resolution photos, a 24-month ownership cost forecast, and a final
            CarLens Confidence Score&trade; with a clear buy, negotiate, or walk-away recommendation. If the car
            checks out, you buy with total peace of mind. If it doesn&rsquo;t, you walk away or use our documented
            findings to negotiate a fairer price &mdash; often saving far more than the cost of the inspection
            itself.
          </p>
          <p>
            Whether you found the vehicle at a franchise dealer, an independent lot, or through a
            <Link to="/services/private-seller-inspection" className="text-orange-400 hover:text-orange-300 underline underline-offset-2"> private seller</Link> or
            <Link to="/services/facebook-marketplace-inspection" className="text-orange-400 hover:text-orange-300 underline underline-offset-2"> Facebook Marketplace</Link> listing,
            CarLens comes to the car so you never have to move it or take time off work. Want a first opinion
            before you even schedule a visit? Book a <Link to="/firstlook" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">CarLens FirstLook&trade;</Link> 1-on-1 consultation, or explore buying tips in the <Link to="/academy" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">CarLens Academy</Link>. Buying from another state? See our <Link to="/out-of-state-car-inspection" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">out-of-state car inspection</Link> service, or explore the <Link to="/vehicles-we-inspect" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">vehicles we inspect</Link>.
          </p>
        </div>
      </section>

      {/* Areas We Serve — internal links to location pages */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="areas-we-serve-section">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
            Vehicle Inspection Service Areas
          </h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
            CarLens brings mobile pre-purchase inspections to buyers across Northeast Florida. Choose your city
            to learn more about inspections in your area.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { slug: 'jacksonville', name: 'Jacksonville' },
            { slug: 'st-augustine', name: 'St. Augustine' },
            { slug: 'orange-park', name: 'Orange Park' },
            { slug: 'fleming-island', name: 'Fleming Island' },
            { slug: 'middleburg', name: 'Middleburg' },
            { slug: 'fernandina-beach', name: 'Fernandina Beach' },
            { slug: 'ponte-vedra-beach', name: 'Ponte Vedra Beach' },
            { slug: 'green-cove-springs', name: 'Green Cove Springs' },
            { slug: 'palm-coast', name: 'Palm Coast' },
            { slug: 'amelia-island', name: 'Amelia Island' },
            { slug: 'gainesville', name: 'Gainesville' },
            { slug: 'daytona-beach', name: 'Daytona Beach' },
          ].map((loc) => (
            <Link
              key={loc.slug}
              to={`/locations/${loc.slug}`}
              className="group flex items-center gap-2 rounded-xl border border-zinc-800 bg-[#161618] px-4 py-4 hover:border-orange-500/50 hover:bg-[#1c1c1f] transition-all"
              data-testid={`area-link-${loc.slug}`}
            >
              <MapPin className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span className="text-sm md:text-base font-medium text-zinc-200 group-hover:text-white">{loc.name}</span>
            </Link>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/locations" className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold" data-testid="all-locations-link">
            View all service areas <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Popular Inspection Services — internal links to service pages */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="popular-services-section">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
            Our Inspection Services
          </h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
            Independent, mobile inspections tailored to how and where you&rsquo;re buying.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { slug: 'pre-purchase-inspection', name: 'Pre-Purchase Inspection', desc: 'The complete 200-point evaluation before you buy any used car, truck, or SUV.', Icon: ShieldCheck },
            { slug: 'private-seller-inspection', name: 'Private Seller Inspection', desc: 'Independent verification before you pay a private party with no warranty.', Icon: FileText },
            { slug: 'facebook-marketplace-inspection', name: 'Facebook Marketplace Inspection', desc: 'Vet an online listing in person before you meet up and hand over cash.', Icon: ScanLine },
            { slug: 'dealer-lot-inspection', name: 'Dealer Lot Inspection', desc: 'An unbiased second opinion on a car sitting on a dealership lot.', Icon: BadgeCheck },
            { slug: 'firstlook-remote-analysis', name: 'FirstLook Consultation', desc: 'A private 30-minute video consultation with a CarLens expert before you buy.', Icon: Gauge },
            { slug: 'out-of-state-car-inspection', name: 'Out-of-State Buyer Inspection', desc: 'Buying a Florida car from another state? We inspect it before you travel or pay.', Icon: MapPin },
            { slug: 'post-purchase-inspection', name: 'Post-Purchase Inspection', desc: 'Just bought a car? Know exactly what to maintain and what to fix first.', Icon: Wrench },
          ].map((s) => (
            <Link
              key={s.slug}
              to={s.slug === 'firstlook-remote-analysis' ? '/firstlook' : s.slug === 'out-of-state-car-inspection' ? '/out-of-state-car-inspection' : `/services/${s.slug}`}
              className="group rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6 hover:border-orange-500/50 transition-all"
              data-testid={`service-link-${s.slug}`}
            >
              <div className="w-11 h-11 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                <s.Icon className="w-5 h-5 text-orange-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1 group-hover:text-orange-300 transition-colors">{s.name}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Recent Photos From Our Inspections — fresh, unique proof content */}
      {recentPhotos.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="home-recent-photos-section">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <Camera className="w-6 h-6 text-orange-500" />
              <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">From the Field</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
              Recent Photos From Our Inspections
            </h2>
            <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
              Real photos from vehicles our certified inspectors recently evaluated across Northeast Florida. Every CarLens report includes 30&ndash;50 detailed images like these.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {recentPhotos.map((p, i) => (
              <Link
                key={i}
                to="/recent-inspections"
                className="group relative aspect-square overflow-hidden rounded-xl border border-zinc-800 hover:border-orange-500/50 transition-colors"
                data-testid={`home-inspection-photo-${i}`}
              >
                <img
                  src={p.url}
                  alt={`Photo from a CarLens pre-purchase inspection of a ${p.vehicle}${p.city ? ` in ${p.city}` : ''}`}
                  className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="absolute bottom-2 left-2 right-2 text-xs font-medium text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">{p.vehicle}</span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/recent-inspections" className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold" data-testid="home-view-all-inspections-link">
              View the full inspection photo gallery <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="home-faq-section">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <HelpCircle className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Pre-Purchase Inspection FAQs
          </h2>
          <p className="text-zinc-400 text-base md:text-lg mt-3">
            Everything Jacksonville-area car buyers ask before booking with CarLens.
          </p>
        </div>
        <div className="space-y-4">
          {HOME_FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-2xl border border-zinc-800 bg-[#161618] px-6 py-5 hover:border-orange-500/40 transition-colors"
              data-testid={`home-faq-${i}`}
            >
              <summary className="flex items-center justify-between cursor-pointer list-none text-white font-semibold text-base md:text-lg">
                <span>{f.q}</span>
                <ArrowRight className="w-5 h-5 text-orange-400 flex-shrink-0 ml-4 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-4 text-zinc-300 text-base leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Stay Connected Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-r from-zinc-700/20 to-zinc-600/20 rounded-3xl border border-zinc-600/30 p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
            Stay Connected With Us
          </h3>
          <p className="text-zinc-300 mb-6">
            Follow us on Facebook for inspection tips, car buying advice, and exclusive offers
          </p>
          <a 
            href="https://www.facebook.com/profile.php?id=61583275422342" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-zinc-700 hover:bg-zinc-800 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
            data-testid="facebook-connect-btn"
          >
            <Facebook className="w-6 h-6" />
            Follow Us on Facebook
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="bg-gradient-to-r from-orange-600/20 to-orange-500/20 rounded-3xl border border-orange-500/30 p-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Ready to Inspect Your Next Car?
          </h2>
          <p className="text-zinc-300 text-lg mb-8">
            Book an inspection today and buy with confidence
          </p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg"
            onClick={() => navigate('/book')}
            data-testid="book-inspection-cta-btn"
          >
            Book Inspection Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-24">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold tracking-wide" style={{ fontFamily: 'Space Grotesk' }}>CarLens Automotive Intelligence</p>
              <p className="text-zinc-500 text-sm">© 2025 CarLens Automotive Intelligence. All rights reserved.</p>
            </div>
            <a 
              href="https://www.facebook.com/profile.php?id=61583275422342" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-400 hover:text-zinc-600 transition-colors"
              data-testid="facebook-footer-btn"
            >
              <Facebook className="w-5 h-5" />
              <span>Follow us on Facebook</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;